// CTC calculator (Accounts → Salaries → CTC calculator): work from a monthly in-hand salary to the annual package
// (cost to company) and back. Estimates for offers, not payroll: statutory rates as of Oct 2026, income tax on the
// default regime with no declarations. Pure functions so the page and a quick node check share them.
//
// India   PF 12% + 12% of PF wage (basic, capped at ₹15,000 unless "PF on full basic"), employer EDLI + admin ~1%;
//         ESI 0.75% / 3.25% while gross ≤ ₹21,000; professional tax by state; gratuity 4.81% of basic (accrued);
//         TDS on the new regime (standard deduction ₹75,000, rebate to ₹12L taxable with marginal relief, 4% cess).
//         Labour codes: basic at least 50% of gross, so 50% is the default.
// Sri Lanka  EPF 8% employee / 12% employer and ETF 3% on gross; APIT monthly slabs from April 2025.
// UAE     No income tax or PF for expat staff; gratuity accrues 21 days' basic a year (first 5 years);
//         medical insurance and visa costs are the employer's.

const round = (n) => Math.round(n);

export const COUNTRIES = {
  IN: { label: "India", currency: "INR", basicPct: 50 },
  LK: { label: "Sri Lanka", currency: "LKR", basicPct: 60 },
  AE: { label: "UAE", currency: "AED", basicPct: 60 },
};

export function countryFor(currency) {
  return currency === "LKR" ? "LK" : currency === "AED" ? "AE" : "IN";
}

// Professional tax a month by gross, per state
function professionalTax(state, gross) {
  if (state === "KA") return gross >= 25000 ? 200 : 0;
  if (state === "TS") return gross > 20000 ? 200 : gross > 15000 ? 150 : 0;
  return 0;
}

// India new regime, FY 2026-27 slabs; returns annual tax
export function indiaTax(annualTaxable) {
  const slabs = [[400000, 0], [800000, 0.05], [1200000, 0.10], [1600000, 0.15], [2000000, 0.20], [2400000, 0.25], [Infinity, 0.30]];
  let tax = 0, prev = 0;
  for (const [top, rate] of slabs) {
    if (annualTaxable > prev) tax += (Math.min(annualTaxable, top) - prev) * rate;
    prev = top;
  }
  if (annualTaxable <= 1200000) tax = 0;                               // section 87A rebate
  else tax = Math.min(tax, annualTaxable - 1200000);                    // marginal relief just above ₹12L
  return tax * 1.04;
}

// Sri Lanka APIT a month on monthly gross (from 1 April 2025)
export function sriLankaApit(gross) {
  const bands = [[150000, 0], [83333, 0.06], [41667, 0.18], [41667, 0.24], [41667, 0.30], [Infinity, 0.36]];
  let left = gross, tax = 0;
  for (const [size, rate] of bands) {
    const part = Math.min(left, size);
    tax += part * rate;
    left -= part;
    if (left <= 0) break;
  }
  return tax;
}

export const DEFAULTS = {
  IN: { basicPct: 50, pfCapped: true, state: "KA", insuranceYear: 0, bonusYear: 0, includeTax: true },
  LK: { basicPct: 60, insuranceYear: 0, bonusYear: 0, includeTax: true },
  AE: { basicPct: 60, insuranceYear: 0, visaYear: 0, bonusYear: 0 },
};

// Everything for one monthly gross
export function fromGross(country, gross, o) {
  const basic = gross * (o.basicPct || 50) / 100;
  const lines = { gross, basic, allowances: gross - basic };
  const emp = [];      // deducted from pay   [label, monthly]
  const er = [];       // employer pays on top [label, monthly]
  if (country === "IN") {
    const pfWage = o.pfCapped ? Math.min(basic, 15000) : basic;
    emp.push(["PF (employee 12%)", pfWage * 0.12]);
    er.push(["PF (employer 12%)", pfWage * 0.12], ["PF admin + EDLI (1%)", pfWage * 0.01]);
    if (gross <= 21000) {
      emp.push(["ESI (employee 0.75%)", gross * 0.0075]);
      er.push(["ESI (employer 3.25%)", gross * 0.0325]);
    }
    const pt = professionalTax(o.state, gross);
    if (pt) emp.push(["Professional tax", pt]);
    er.push(["Gratuity (4.81% of basic)", basic * 0.0481]);
    if (o.includeTax) {
      const taxable = Math.max(0, gross * 12 + (o.bonusYear || 0) - 75000 - pt * 12);
      const tds = indiaTax(taxable) / 12;
      if (tds > 0) emp.push(["Income tax (TDS estimate)", tds]);
    }
  } else if (country === "LK") {
    emp.push(["EPF (employee 8%)", gross * 0.08]);
    er.push(["EPF (employer 12%)", gross * 0.12], ["ETF (employer 3%)", gross * 0.03]);
    if (o.includeTax) {
      const apit = sriLankaApit(gross);
      if (apit > 0) emp.push(["APIT (income tax estimate)", apit]);
    }
  } else {
    er.push(["Gratuity (21 days' basic a year)", basic * 21 / 30 / 12]);
  }
  const annualExtras = [];
  if (o.insuranceYear) annualExtras.push(["Health insurance", +o.insuranceYear]);
  if (country === "AE" && o.visaYear) annualExtras.push(["Visa and labour card", +o.visaYear]);
  if (o.bonusYear) annualExtras.push(["Annual bonus / variable", +o.bonusYear]);
  const empR = emp.map(([l, v]) => [l, round(v)]);
  const erR = er.map(([l, v]) => [l, round(v)]);
  const deductions = empR.reduce((s, [, v]) => s + v, 0);
  const employer = erR.reduce((s, [, v]) => s + v, 0);
  const extras = annualExtras.reduce((s, [, v]) => s + v, 0);
  return {
    ...lines,
    employeeDeductions: empR,
    employerContributions: erR,
    annualExtras,
    inHand: round(gross - deductions),
    monthlyCost: round(gross + employer),
    ctc: round((gross + employer) * 12 + extras),
  };
}

// Solve for the gross that gives a target (in-hand a month, or CTC a year); both rise with gross
function solve(country, o, target, key) {
  let lo = 0, hi = Math.max(1000, target * 2);
  while (fromGross(country, hi, o)[key] < target) hi *= 2;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (fromGross(country, mid, o)[key] < target) lo = mid; else hi = mid;
  }
  return fromGross(country, Math.ceil(hi), o);
}

export const fromInHand = (country, inHand, o) => solve(country, o, inHand, "inHand");
export const fromCtc = (country, ctc, o) => solve(country, o, ctc, "ctc");
